/**
 * Recorte de imagem para avatar (teste físico, item 15).
 *
 * Uma foto 9:16 do rosto virava um avatar cortando a cara: o recorte era
 * automático, pelo centro. Aqui a pessoa arrasta a foto por trás de um
 * círculo fixo e amplia com a pinça ou o controle — o que está no círculo é
 * o que vira o avatar. A saída é um JPEG quadrado (512 px), pronto para o
 * mesmo envio de sempre.
 *
 * A imagem é decodificada com a orientação EXIF aplicada (`from-image`): a
 * foto deitada não aparece deitada. Zoom mínimo é o que cobre o círculo —
 * não há como deixar borda vazia dentro do avatar.
 */
import { useEffect, useRef, useState } from 'react';

import { Sheet } from './Sheet';

const LADO_SAIDA = 512;
const ZOOM_MAXIMO = 4;

interface Vista {
  /** Escala aplicada à imagem (1 = cobre exatamente o círculo). */
  zoom: number;
  /** Deslocamento do centro da imagem em relação ao centro do círculo, em px da tela. */
  dx: number;
  dy: number;
}

function limitar(vista: Vista, imagem: { w: number; h: number }, diametro: number): Vista {
  const base = diametro / Math.min(imagem.w, imagem.h);
  const escala = base * vista.zoom;
  const larg = imagem.w * escala;
  const alt = imagem.h * escala;
  // A imagem nunca deixa o círculo com borda vazia: o centro pode andar só
  // até a metade do que sobra de cada lado.
  const maxX = Math.max(0, (larg - diametro) / 2);
  const maxY = Math.max(0, (alt - diametro) / 2);
  return {
    zoom: Math.min(ZOOM_MAXIMO, Math.max(1, vista.zoom)),
    dx: Math.min(maxX, Math.max(-maxX, vista.dx)),
    dy: Math.min(maxY, Math.max(-maxY, vista.dy)),
  };
}

export function RecorteDeImagem({
  arquivo,
  titulo = 'Ajustar a foto',
  onCancelar,
  onConfirmar,
  ocupado = false,
}: {
  arquivo: Blob;
  titulo?: string;
  onCancelar: () => void;
  onConfirmar: (recorte: Blob) => void;
  ocupado?: boolean;
}) {
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);
  const [vista, setVista] = useState<Vista>({ zoom: 1, dx: 0, dy: 0 });
  const canvas = useRef<HTMLCanvasElement>(null);
  const diametro = useRef(260);
  const toque = useRef<{ x: number; y: number; dx: number; dy: number; dist?: number; zoom?: number } | null>(null);

  useEffect(() => {
    let vivo = true;
    void createImageBitmap(arquivo, { imageOrientation: 'from-image' } as ImageBitmapOptions).then((b) => {
      if (vivo) setBitmap(b);
      else b.close();
    });
    return () => {
      vivo = false;
    };
  }, [arquivo]);

  useEffect(() => () => bitmap?.close(), [bitmap]);

  // Redesenha a cada mudança de vista.
  useEffect(() => {
    const el = canvas.current;
    if (!el || !bitmap) return;
    const d = diametro.current;
    const dpr = window.devicePixelRatio || 1;
    el.width = d * dpr;
    el.height = d * dpr;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, d, d);
    const v = limitar(vista, { w: bitmap.width, h: bitmap.height }, d);
    const escala = (d / Math.min(bitmap.width, bitmap.height)) * v.zoom;
    const larg = bitmap.width * escala;
    const alt = bitmap.height * escala;
    ctx.drawImage(bitmap, d / 2 - larg / 2 + v.dx, d / 2 - alt / 2 + v.dy, larg, alt);
  }, [bitmap, vista]);

  const mover = (dx: number, dy: number) => {
    if (!bitmap) return;
    setVista((v) => limitar({ ...v, dx, dy }, { w: bitmap.width, h: bitmap.height }, diametro.current));
  };
  const ampliar = (zoom: number) => {
    if (!bitmap) return;
    setVista((v) => limitar({ ...v, zoom }, { w: bitmap.width, h: bitmap.height }, diametro.current));
  };

  function confirmar() {
    if (!bitmap) return;
    const saida = document.createElement('canvas');
    saida.width = LADO_SAIDA;
    saida.height = LADO_SAIDA;
    const ctx = saida.getContext('2d');
    if (!ctx) return;
    const d = diametro.current;
    const v = limitar(vista, { w: bitmap.width, h: bitmap.height }, d);
    const escalaTela = (d / Math.min(bitmap.width, bitmap.height)) * v.zoom;
    // O que está no círculo, em coordenadas da imagem original.
    const origemX = (d / 2 - (bitmap.width * escalaTela) / 2 + v.dx) * -1;
    const origemY = (d / 2 - (bitmap.height * escalaTela) / 2 + v.dy) * -1;
    const sx = origemX / escalaTela;
    const sy = origemY / escalaTela;
    const sLado = d / escalaTela;
    ctx.drawImage(bitmap, sx, sy, sLado, sLado, 0, 0, LADO_SAIDA, LADO_SAIDA);
    saida.toBlob((blob) => blob && onConfirmar(blob), 'image/jpeg', 0.88);
  }

  const distancia = (t: { [index: number]: { clientX: number; clientY: number } }) => {
    const a = t[0]!;
    const b = t[1]!;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };

  return (
    <Sheet titleId="recorte-titulo" onFechar={onCancelar} testId="recorte">
      <h2 className="sheet__title" id="recorte-titulo">{titulo}</h2>
      <p className="sheet__text">Arraste para posicionar e use a pinça ou o controle para ampliar. O que está no círculo vira a foto.</p>
      <div className="recorte">
        <canvas
          ref={canvas}
          className="recorte__canvas"
          style={{ width: diametro.current, height: diametro.current }}
          data-testid="recorte-canvas"
          onPointerDown={(e) => {
            if (e.pointerType === 'touch') return; // o toque é tratado pelos eventos de touch, para a pinça
            (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
            toque.current = { x: e.clientX, y: e.clientY, dx: vista.dx, dy: vista.dy };
          }}
          onPointerMove={(e) => {
            if (!toque.current || e.pointerType === 'touch') return;
            mover(toque.current.dx + (e.clientX - toque.current.x), toque.current.dy + (e.clientY - toque.current.y));
          }}
          onPointerUp={() => {
            toque.current = null;
          }}
          onTouchStart={(e) => {
            // Arrastar a foto não pode arrastar a folha (que fecha ao puxar para baixo).
            e.stopPropagation();
            const t = e.touches;
            if (t.length === 2) toque.current = { x: 0, y: 0, dx: vista.dx, dy: vista.dy, dist: distancia(t), zoom: vista.zoom };
            else if (t.length === 1) toque.current = { x: t[0]!.clientX, y: t[0]!.clientY, dx: vista.dx, dy: vista.dy };
          }}
          onTouchMove={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const t = e.touches;
            const inicio = toque.current;
            if (!inicio) return;
            if (t.length === 2 && inicio.dist && inicio.zoom) ampliar(inicio.zoom * (distancia(t) / inicio.dist));
            else if (t.length === 1 && inicio.dist === undefined) mover(inicio.dx + (t[0]!.clientX - inicio.x), inicio.dy + (t[0]!.clientY - inicio.y));
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            toque.current = null;
          }}
        />
        <span className="recorte__mascara" aria-hidden="true" />
      </div>
      <label className="recorte__zoom">
        <span className="nv-visually-hidden">Ampliar</span>
        <input type="range" min={1} max={ZOOM_MAXIMO} step={0.01} value={vista.zoom} onChange={(e) => ampliar(Number(e.target.value))} data-testid="recorte-zoom" />
      </label>
      <div className="fin__ctas">
        <button type="button" className="nv-btn nv-btn--secondary" onClick={onCancelar}>
          Cancelar
        </button>
        <button type="button" className="nv-btn nv-btn--primary" disabled={!bitmap || ocupado} onClick={confirmar} data-testid="recorte-usar">
          {ocupado ? 'Enviando…' : 'Usar este recorte'}
        </button>
      </div>
    </Sheet>
  );
}
